import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Container, Eyebrow, DisplayH1, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, MapPin, Sparkles, ArrowUpRight } from "lucide-react";

const SPECIALTIES = [
  "All", "Puppy", "Behavior", "Reactivity", "Aggression", "Service Dogs",
  "Working Dogs", "Detection", "Protection", "Rescue", "Business", "Youth"
];

export default function Alumni() {
  const { user } = useAuth();
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [specialty, setSpecialty] = useState("All");
  const [me, setMe] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (query) params.q = query;
    if (specialty && specialty !== "All") params.specialty = specialty;
    api.get("/alumni", { params }).then((r) => setAlumni(r.data.alumni || [])).finally(() => setLoading(false));
  };
  useEffect(load, [specialty]);
  useEffect(() => {
    if (user) api.get("/alumni/me/profile").then((r) => setMe(r.data)).catch(() => setMe({}));
  }, [user]);

  const total = alumni.length;

  return (
    <>
      <Section testid="alumni-hero" className="pt-40 pb-10">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Eyebrow>Certified Wolfkeeper Alumni · Global Directory</Eyebrow>
              <DisplayH1 className="mt-6 max-w-3xl">Find a trainer trained by the University.</DisplayH1>
              <p className="mt-6 max-w-2xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
                Every alumnus in this directory has been certified through Wolfkeeper and opted-in to
                be found by families and dogs seeking help.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/alumni/join"
                className="btn-ghost"
                data-testid="alumni-join"
              >
                {me && me.is_public ? "Manage my profile" : "Join the directory"}
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      {/* Search / filters */}
      <Section testid="alumni-filters" className="pb-4">
        <Container>
          <div className="card-lux p-4 md:p-5 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
            <div className="flex-1 flex items-center gap-3 hairline rounded-full px-4 py-2">
              <Search size={16} className="text-[color:var(--wk-gold)]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search name, city, specialty…"
                data-testid="alumni-search"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--wk-muted)]"
              />
              <button onClick={load} className="btn-gold h-9 text-xs">Search</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSpecialty(s)}
                  data-testid={`alumni-spec-${s}`}
                  className={`px-3 py-1.5 rounded-full text-xs border ${specialty === s ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]" : "border-white/15 text-[color:var(--wk-bone-dim)] hover:border-[color:var(--wk-gold)] hover:text-[color:var(--wk-gold)]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 text-xs text-[color:var(--wk-bone-dim)]">{loading ? "Searching…" : `${total} alumni · ${specialty === "All" ? "all specialties" : specialty}`}</div>
        </Container>
      </Section>

      {/* Directory grid */}
      <Section testid="alumni-grid" className="pb-32">
        <Container>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {alumni.map((a, i) => (
              <Reveal key={a.alumni_id || i} delay={0.03 * (i % 6)}>
                <Link to={`/alumni/${a.alumni_id}`} className="card-lux overflow-hidden block group h-full">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={a.photo_url} alt={a.display_name} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 cinema-overlay" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="font-serif-display text-2xl leading-tight">{a.display_name}</div>
                      <div className="mt-1 text-xs text-[color:var(--wk-bone-dim)]">{a.headline}</div>
                    </div>
                  </div>
                  <div className="p-5 flex flex-col gap-3">
                    <div className="flex items-center gap-2 text-xs text-[color:var(--wk-bone-dim)]">
                      <MapPin size={12} className="text-[color:var(--wk-gold)]" /> {a.city}, {a.country}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(a.specialties || []).slice(0, 3).map((s) => (
                        <span key={s} className="px-2 py-0.5 rounded-full text-[10px] border border-white/10 text-[color:var(--wk-bone-dim)]">{s}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-[10px] overline text-[color:var(--wk-gold)]">{a.program}</div>
                      <span className="w-8 h-8 rounded-full hairline flex items-center justify-center group-hover:bg-[color:var(--wk-gold)] group-hover:text-black text-[color:var(--wk-gold)] transition-all">
                        <ArrowUpRight size={12} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          {!loading && alumni.length === 0 && (
            <div className="card-lux p-10 text-center max-w-2xl mx-auto">
              <Sparkles className="text-[color:var(--wk-gold)] mx-auto" />
              <div className="mt-4 font-serif-display text-2xl">No matches yet.</div>
              <p className="mt-2 text-[color:var(--wk-bone-dim)]">Try a different search or specialty.</p>
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}
